import {
  ButtonERP,
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { faArrowLeft, faRotateRight } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GetMovimientosTarjetaBancariaByFiltersDtoProps } from "api/DTOs/GetMovimientosTarjetaBancariaByFiltersDto"
import { useTarjetaBancaria } from "api/TarjetaBancaria.hook"
import {
  GetMovimientosTarjetaBancariaQueryParamsProps,
  getMovimientosTarjetaById,
  getMovimientosTarjetaExportarExcel,
  getSyncTarjetaBancariaById,
  movimientosTarjetaByIdFiltersCall,
} from "api/TarjetaBancaria.service"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import { handleDirectDownloadExcel } from "common/context/DownloadFileProvider"
import { CURRENT_DATE_FORMAT_WITH_HOURS_LONG } from "common/utils/constants"
import { formatDate } from "common/utils/date"
import { DATE_YYYY_MM_DD } from "constants/formatoFechas"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { useAfterbanksNotifications } from "context/afterbankContext"
import { endOfMonth, format, startOfMonth } from "date-fns"
import { ColoredNumber } from "features/Contabilidad/components/ColoredNumber"
import {
  DateRangeModalFechas,
  ModalFechas,
} from "features/Contabilidad/pages/LibroDiario/components/Modales/ModalFechas"
import { useFilterMenu } from "hooks/useFilterMenu"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useParams } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { DEFAULT_BANK_LOGO_URL } from "../../constants"
import { FilterMenu } from "./FilterMenu"
import {
  MOVIMIENTOS_BANCARIOS_COLUMNS,
  TARJETA_DETAIL_FILTERS,
} from "./contants"

const TarjetaDetail = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const params = useParams()
  const currentMonthExportExcel = {
    min: startOfMonth(new Date()),
    max: endOfMonth(new Date()),
  }
  const { activeCompany } = useUsuarioEmpresaActiva()

  const defaultFilters = () => {
    const storedData = sessionStorage.getItem("tarjetaDetailFilters")
    const defaultFilters = storedData ? JSON.parse(storedData) : {}
    return params.id !== defaultFilters?.id
      ? TARJETA_DETAIL_FILTERS(
          params?.id,
          activeCompany?.id,
          defaultFilters?.pagination?.itemsPerPage
        )
      : defaultFilters
  }
  const [bankDetailsFilter, setBankDetailsFilter] = useState(defaultFilters())
  const [date, setDate] = useState<DateRangeModalFechas>(
    currentMonthExportExcel
  )
  const [showExportDatesModal, setShowExportDatesModal] = useState(false)

  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortingCriteria, setSortingCriteria] = useState<
    GetMovimientosTarjetaBancariaQueryParamsProps["SortingCriteria"]
  >({
    sort: undefined,
    property: undefined,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const dtRef =
    useRef<
      DataTablePrimeReact<GetMovimientosTarjetaBancariaByFiltersDtoProps[]>
    >(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }

  const { tarjetaDetail, tarjetaDetailIsLoading, tarjetaDetailIsFetching } =
    useTarjetaBancaria({
      bankDetailsFilterId: bankDetailsFilter?.id,
    })

  const { addNotification, getNotificationIdCompleted } =
    useAfterbanksNotifications()

  const fetchTarjetaMovimientos = useCallback(() => {
    return async (
      queryParams: GetMovimientosTarjetaBancariaQueryParamsProps
    ) => {
      movimientosTarjetaByIdFiltersCall?.cancel()
      if (queryParams?.Id) {
        return getMovimientosTarjetaById(queryParams.Id, queryParams)
      }
      return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarjetaDetail?.fechaUltimaActualizacion])

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchTarjetaMovimientos,
    })

  const handleRedirectToList = () => {
    history.push(ROUTES.TARJETAS)
  }

  const exportToExcel = async () => {
    if (params.id) {
      const { data } = await getMovimientosTarjetaExportarExcel(params.id, {
        FechaLowerLimit: date?.min ? format(date.min, DATE_YYYY_MM_DD) : "",
        FechaUpperLimit: date?.max ? format(date.max, DATE_YYYY_MM_DD) : "",
      })
      handleDirectDownloadExcel(data, `${t("exportarXlxs.movementCard")}.xlsx`)
    }
  }

  const handleBankCardSync = async () => {
    addNotification({
      id: bankDetailsFilter?.id,
      idExterno: bankDetailsFilter?.idExterno,
    })
    try {
      await getSyncTarjetaBancariaById(bankDetailsFilter.id)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      getNotificationIdCompleted(bankDetailsFilter.id)
    }
  }

  useEffect(() => {
    if (tarjetaDetail?.idExterno) {
      const updatedFilters = {
        ...bankDetailsFilter,
        idExterno: tarjetaDetail.idExterno,
      }
      setBankDetailsFilter(updatedFilters)
      sessionStorage.setItem(
        "tarjetaDetailFilters",
        JSON.stringify(updatedFilters)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarjetaDetail])

  useEffect(() => {
    const updateFilters = {
      ...bankDetailsFilter,
      pagination,
    }
    sessionStorage.setItem(
      "tarjetaDetailFilters",
      JSON.stringify(updateFilters)
    )
  }, [pagination, bankDetailsFilter])

  useEffect(() => {
    if (
      activeCompany.id &&
      activeCompany.id !== bankDetailsFilter.activeCompanyId
    ) {
      handleRedirectToList()
      sessionStorage.removeItem("tarjetaDetailFilters")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany.id])

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
    fetchTableData()
  }

  return (
    <div className='tw-flex tw-flex-col tw-order-2 tw-z-10 tw-relative tw-w-full tw-max-h-screen tw-h-full tw-bg-gray tw-overflow-x-hidden tw-overflow-y-hidden custom-scrollbar'>
      <div className='tw-h-fit tw-p-4 tw-w-full tw-shadow-md tw-bg-white'>
        <div className='tw-h-11 tw-flex tw-flex-row tw-items-center tw-justify-between'>
          <div className='tw-flex tw-flex-row tw-flex-1 tw-mr-10 tw-items-center'>
            <ButtonERP
              customize={{
                type: "white",
                size: "md",
              }}
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={() => history.push(ROUTES.TARJETAS)}
            />
            <div className='tw-w-16 tw-h-full tw-mx-3 tw-flex tw-items-center tw-justify-center'>
              <img
                className={`${
                  tarjetaDetail?.logo
                    ? "tw-h-11 tw-scale-150"
                    : "tw-w-11 tw-h-11"
                }`}
                src={tarjetaDetail?.logo || DEFAULT_BANK_LOGO_URL}
                alt=''
              />
            </div>
            <div className='tw-flex tw-flex-col tw-flex-1 tw-h-full tw-items-start'>
              {tarjetaDetailIsLoading ? (
                <Skeleton
                  height='24px'
                  width='120px'
                />
              ) : (
                <span className='tw-text-nowrap tw-overflow-hidden tw-text-ellipsis tw-max-w-120 tw-text-xl tw-font-medium'>
                  {`${
                    tarjetaDetail?.descripcion || tarjetaDetail?.nombre
                      ? `${
                          tarjetaDetail?.nombre
                            ? tarjetaDetail?.nombre
                            : tarjetaDetail?.descripcion
                        }${tarjetaDetail?.entidadBancaria ? " - " : ""}`
                      : ""
                  }${tarjetaDetail?.entidadBancaria ?? ""}`}
                </span>
              )}
              {tarjetaDetailIsLoading ? (
                <Skeleton
                  height='24px'
                  width='120px'
                />
              ) : (
                <span className='tw-text-base'>{tarjetaDetail?.numero}</span>
              )}
            </div>
          </div>

          <div className='tw-flex tw-flex-row tw-px-10 tw-items-center tw-border-l tw-border-r tw-border-neutral-25'>
            <div className='tw-flex tw-flex-col tw-w-auto tw-h-full tw-items-start tw-text-nowrap'>
              <div className='tw-flex tw-flex-row tw-flex-nowrap tw-text-base'>
                {t("bancos.cardsTable.detail.lastUpdate")}
                {tarjetaDetailIsLoading || tarjetaDetailIsFetching ? (
                  <Skeleton
                    height='24px'
                    width='153px'
                    className='tw-ml-1'
                  />
                ) : (
                  <span className='tw-font-medium tw-ml-2'>
                    {tarjetaDetail?.fechaUltimaActualizacion
                      ? formatDate(
                          tarjetaDetail?.fechaUltimaActualizacion,
                          CURRENT_DATE_FORMAT_WITH_HOURS_LONG
                        )
                      : "-"}
                  </span>
                )}
              </div>
              {/* // todo: descomentar cuando este el back
              <div className='tw-flex tw-flex-row tw-flex-nowrap tw-text-sm'>
                {t("bancos.cardsTable.detail.authorisationUpdate")}
                <span className='tw-ml-2'>17/06/2025</span>
              </div> */}
            </div>
            <div className='tw-ml-10'>
              <ButtonERP
                onClick={handleBankCardSync}
                loading={getNotificationIdCompleted(bankDetailsFilter.id)}
                disabled={getNotificationIdCompleted(bankDetailsFilter.id)}
                icon={<FontAwesomeIcon icon={faRotateRight} />}
                customize={{
                  type: "white",
                  size: "md",
                }}
                label={`${t("bancos.cardsTable.detail.update")}`}
              />
            </div>
          </div>

          <div className='tw-flex tw-flex-col tw-w-auto tw-h-full tw-items-end tw-justify-center tw-ml-10'>
            <span className='tw-text-base tw-flex tw-flex-row tw-flex-nowrap'>
              {t("bancos.cardsTable.detail.balanceDisponible")}
              {tarjetaDetailIsLoading || tarjetaDetailIsFetching ? (
                <Skeleton
                  height='24px'
                  width='82px'
                  className='tw-ml-1'
                />
              ) : (
                <span className='tw-font-medium tw-ml-1'>
                  {tarjetaDetail?.saldo
                    ? ColoredNumber(tarjetaDetail.saldo)
                    : "-"}
                </span>
              )}
            </span>
            {/* 
            // todo: descomentar cuando este el back
            <span className='tw-text-sm'>
              {t("bancos.cardsTable.detail.balance")}
              <span className='tw-ml-1'>{frontendCurrency(3000)}</span>
            </span> */}
          </div>
        </div>
      </div>
      <FilterMenu
        serviceQuery={setServiceQuery}
        sortingCriteria={sortingCriteria}
        skipCount={pagination.skipCount}
        maxResultCount={pagination.itemsPerPage}
        resetPagination={handleResetPagination}
        bankDetailsFilter={bankDetailsFilter}
        setBankDetailsFilter={setBankDetailsFilter}
        exportToExcel={() => setShowExportDatesModal(true)}
      />
      <div className='tw-flex tw-flex-col tw-h-[calc(100%-148px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={MOVIMIENTOS_BANCARIOS_COLUMNS({
              t,
            })}
            className='tw-h-full !tw-z-0'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            lazy
            dataKey='id'
            scrollable
            size='small'
            loading={isLoading}
            emptyMessage={t("primeReact.table.emptyMessage")}
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>

      <ModalFechas
        isOpen={showExportDatesModal}
        date={date}
        setDate={setDate}
        onCancel={() => {
          setShowExportDatesModal(false)
          setDate(currentMonthExportExcel)
        }}
        onConfirm={exportToExcel}
        text={`${t("diario.exportModal.dateRange")}`}
      />
    </div>
  )
}

export default TarjetaDetail
